import {
	CheckCircleOutlined,
	DeleteOutlined,
	EditOutlined,
	MinusCircleOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Form,
	Input,
	Modal,
	message,
	Popconfirm,
	Select,
	Space,
	Spin,
	Switch,
	Tag,
} from "antd";
import { useMemo, useRef, useState } from "react";
import { parseApiError } from "@/shared/utils/error";
import { SmsNavigation } from "../components/SmsNavigation";
import {
	createSmsTemplate,
	deleteSmsTemplate,
	getSmsTemplates,
	updateSmsTemplate,
} from "../services/sms.api";
import type { BackendSmsTemplate } from "../services/sms.mapper";

const templateTypeOptions = [
	{ label: "To'lov eslatmasi (Reminder)", value: "payment_reminder" },
	{ label: "Muddati o'tgan to'lov (Overdue)", value: "overdue" },
	{ label: "Xush kelibsiz (Welcome)", value: "welcome" },
	{ label: "Ixtiyoriy (Custom)", value: "custom" },
];

export function SmsTemplatesPage() {
	const queryClient = useQueryClient();
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 10;

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState<BackendSmsTemplate | null>(null);
	const [form] = Form.useForm();
	const textAreaRef = useRef<any>(null);

	const slugify = (text: string) => {
		return text
			.toLowerCase()
			.replace(/o'/g, "o")
			.replace(/g'/g, "g")
			.replace(/sh/g, "sh")
			.replace(/ch/g, "ch")
			.replace(/[^a-z0-9\s_-]/g, "")
			.trim()
			.replace(/\s+/g, "_");
	};

	const handleInsertVariable = (variable: string) => {
		const bodyValue = form.getFieldValue("body") || "";
		const textarea = textAreaRef.current?.input || textAreaRef.current?.resizableTextArea?.textArea;

		if (textarea) {
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const textBefore = bodyValue.substring(0, start);
			const textAfter = bodyValue.substring(end);
			const newValue = textBefore + variable + textAfter;

			form.setFieldsValue({ body: newValue });

			// Move cursor to after the inserted variable
			setTimeout(() => {
				textarea.focus();
				textarea.setSelectionRange(start + variable.length, start + variable.length);
			}, 0);
		} else {
			form.setFieldsValue({ body: bodyValue + variable });
		}
	};

	// 1. Fetch templates
	const { data: templatesData, isLoading } = useQuery({
		queryKey: ["smsTemplates", currentPage],
		queryFn: () => getSmsTemplates({ page: currentPage, limit: pageSize }),
	});

	const templates = useMemo(() => templatesData?.items ?? [], [templatesData]);
	const total = templatesData?.meta?.total ?? 0;
	const totalPages = Math.ceil(total / pageSize);

	// 2. Create mutation
	const createMutation = useMutation({
		mutationFn: createSmsTemplate,
		onSuccess: () => {
			message.success("Shablon muvaffaqiyatli yaratildi");
			queryClient.invalidateQueries({ queryKey: ["smsTemplates"] });
			setIsModalOpen(false);
			form.resetFields();
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Shablon yaratishda xatolik");
		},
	});

	// 3. Update mutation
	const updateMutation = useMutation({
		mutationFn: (args: { id: string; data: Parameters<typeof updateSmsTemplate>[1] }) =>
			updateSmsTemplate(args.id, args.data),
		onSuccess: () => {
			message.success("Shablon muvaffaqiyatli yangilandi");
			queryClient.invalidateQueries({ queryKey: ["smsTemplates"] });
			setIsModalOpen(false);
			setEditingTemplate(null);
			form.resetFields();
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Shablonni yangilashda xatolik");
		},
	});

	// 4. Delete mutation
	const deleteMutation = useMutation({
		mutationFn: deleteSmsTemplate,
		onSuccess: () => {
			message.success("Shablon o'chirildi");
			queryClient.invalidateQueries({ queryKey: ["smsTemplates"] });
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Shablonni o'chirishda xatolik");
		},
	});

	const handleOpenCreate = () => {
		setEditingTemplate(null);
		form.resetFields();
		setIsModalOpen(true);
	};

	const handleOpenEdit = (template: BackendSmsTemplate) => {
		setEditingTemplate(template);
		form.setFieldsValue({
			name: template.name,
			code: template.code,
			body: template.body,
			type: template.type,
			isActive: template.isActive,
		});
		setIsModalOpen(true);
	};

	const handleSave = () => {
		form.validateFields().then((values) => {
			const code =
				values.type === "custom"
					? editingTemplate?.code || slugify(values.name || "")
					: values.type;

			const payload = {
				...values,
				code,
			};

			if (editingTemplate) {
				updateMutation.mutate({
					id: editingTemplate.id,
					data: payload as Parameters<typeof updateSmsTemplate>[1],
				});
			} else {
				createMutation.mutate(payload as Parameters<typeof createSmsTemplate>[0]);
			}
		});
	};

	return (
		<div className="crm-sms-templates-page">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS moduli</h1>
					<p>Eskiz integratsiyasi orqali tizim balansi va bildirishnomalarni boshqarish</p>
				</div>
				<div className="crm-head-actions">
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						type="primary"
						onClick={handleOpenCreate}
					>
						Yangi shablon
					</Button>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="templates" />

			{/* Templates Table Card */}
			<div className="crm-card crm-table-card">
				<div className="crm-card-head">
					<h3>SMS shablonlari ro'yxati</h3>
				</div>
				<div className="crm-table-wrap">
					{isLoading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<Spin />
						</div>
					) : (
						<table className="crm-table">
							<thead>
								<tr>
									<th>Shablon nomi</th>
									<th>Kod (Slug)</th>
									<th>Turi</th>
									<th>Matn andozasi</th>
									<th>Holat</th>
									<th style={{ textAlign: "right" }}>Amallar</th>
								</tr>
							</thead>
							<tbody>
								{templates.length === 0 ? (
									<tr>
										<td colSpan={6} style={{ textAlign: "center", padding: "30px 10px" }}>
											<span style={{ color: "var(--crm-ink-3)" }}>Shablonlar topilmadi</span>
										</td>
									</tr>
								) : (
									templates.map((tpl) => (
										<tr key={tpl.id}>
											<td style={{ fontWeight: 600 }}>{tpl.name}</td>
											<td>
												<code>{tpl.code}</code>
											</td>
											<td>
												<span className="crm-user-role" style={{ fontSize: "11px" }}>
													{tpl.type}
												</span>
											</td>
											<td
												style={{
													maxWidth: "300px",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{tpl.body}
											</td>
											<td>
												{tpl.isActive ? (
													<span
														style={{
															color: "#389e0d",
															display: "inline-flex",
															alignItems: "center",
															gap: 4,
														}}
													>
														<CheckCircleOutlined /> Faol
													</span>
												) : (
													<span
														style={{
															color: "#8c8c8c",
															display: "inline-flex",
															alignItems: "center",
															gap: 4,
														}}
													>
														<MinusCircleOutlined /> Faol emas
													</span>
												)}
											</td>
											<td style={{ textAlign: "right" }}>
												<Space>
													<Button
														icon={<EditOutlined />}
														type="text"
														onClick={() => handleOpenEdit(tpl)}
													/>
													<Popconfirm
														title="Shablonni o'chirishni xohlaysizmi?"
														onConfirm={() => deleteMutation.mutate(tpl.id)}
														okText="Ha"
														cancelText="Yo'q"
													>
														<Button icon={<DeleteOutlined />} type="text" danger />
													</Popconfirm>
												</Space>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
				<div className="crm-table-foot">
					<span>Jami {total} ta shablon</span>
					{totalPages > 1 && (
						<div className="crm-pager">
							<button
								type="button"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							>
								‹
							</button>
							{Array.from({ length: totalPages }).map((_, i) => (
								<button
									key={`page-${i + 1}`}
									className={currentPage === i + 1 ? "active" : ""}
									type="button"
									onClick={() => setCurrentPage(i + 1)}
								>
									{i + 1}
								</button>
							))}
							<button
								type="button"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							>
								›
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Create/Edit Modal */}
			<Modal
				cancelText="Bekor"
				okText={editingTemplate ? "Saqlash" : "Yaratish"}
				confirmLoading={createMutation.isPending || updateMutation.isPending}
				open={isModalOpen}
				title={editingTemplate ? "Shablonni tahrirlash" : "Yangi shablon yaratish"}
				width={520}
				styles={{ body: { paddingBottom: 0 } }}
				onCancel={() => setIsModalOpen(false)}
				onOk={handleSave}
			>
				<Form
					form={form}
					initialValues={{ type: "custom", isActive: true }}
					layout="vertical"
					style={{ marginTop: 8 }}
				>
					<div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 12 }}>
						<Form.Item
							label="Shablon nomi"
							name="name"
							rules={[{ required: true, message: "Nomini kiriting" }]}
							style={{ marginBottom: 12 }}
						>
							<Input placeholder="Masalan: To'lov eslatmasi" />
						</Form.Item>

						<Form.Item
							label="Shablon turi"
							name="type"
							rules={[{ required: true, message: "Turini tanlang" }]}
							style={{ marginBottom: 12 }}
						>
							<Select options={templateTypeOptions} />
						</Form.Item>
					</div>

					<Form.Item
						label="Xabar matni"
						name="body"
						rules={[{ required: true, message: "Matnni kiriting" }]}
						style={{ marginBottom: 8 }}
					>
						<Input.TextArea
							ref={textAreaRef}
							placeholder="Hurmatli {client_name}, sizning {due_date} kuni {amount} so'm to'lovingiz bor."
							rows={2}
						/>
					</Form.Item>
					<div style={{ marginBottom: 16, display: "flex", gap: 6, flexWrap: "wrap" }}>
						<span style={{ fontSize: 11, color: "#64748b", width: "100%", marginBottom: 2 }}>
							O'zgaruvchilar (qo'shish uchun bosing):
						</span>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{client_name}")}
						>
							Mijoz ismi
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{amount}")}
						>
							Summa
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{due_date}")}
						>
							Sana
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{room_number}")}
						>
							Xona
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{building_name}")}
						>
							Bino
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{manager_name}")}
						>
							Menejer
						</Tag>
						<Tag
							color="blue"
							style={{ cursor: "pointer", userSelect: "none", marginInlineEnd: 0 }}
							onClick={() => handleInsertVariable("{org_name}")}
						>
							Tashkilot
						</Tag>
					</div>

					<Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 8 }}>
						<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
							<Switch />
							<span style={{ fontWeight: 500, color: "#1e293b" }}>Shablon faol holatda</span>
						</div>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default SmsTemplatesPage;
